import { IonItem, IonToast } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { TopicSearchBox } from '../components/Forms/TopicSearchBox';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconArrowBack, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { Tab, Tabs } from '../components/Tabs.js';
import { useTopicPreferences } from '../hooks/useTopicPreferences';
import { pluralize } from '../util/pluralize.js';
import styles from './TopicPreferences.module.css';

interface TopicPreferencesSectionProps {
	topics: string[];
	type: 'hidden' | 'following';
	onRemoveTopic: (topic: string) => void | Promise<void>;
	onAddTopic: (topic: string) => void | Promise<void>;
	isLoading?: boolean;
	prefillTopic?: string;
}

function TopicPreferencesSection({
	topics,
	type,
	onRemoveTopic,
	onAddTopic,
	isLoading,
	prefillTopic
}: TopicPreferencesSectionProps): JSX.Element {
	const [{ topic }, setState] = useState<{ topic: string }>({ topic: '' });

	useEffect(() => {
		if (topic) {
			setState({ topic: '' });
			onAddTopic(topic);
		}
	}, [topic, onAddTopic]);
	const typeTitle = type === 'hidden' ? 'Hidden' : 'Subscribed';
	const totalTopicsTitle = (
		<h6>
			{topics.length} {type === 'hidden' ? 'Hidden' : 'Subscribed'} {pluralize(topics.length, 'Topic')}
		</h6>
	);
	const setValues = useCallback(({ topic = '' }) => {
		setState({ topic });
	}, []);
	return (
		<div className={styles.topicsSection}>
			<div className={styles.topicsSectionHeaderWrap}>
				<TopicSearchBox topic={topic} nogap setValues={setValues} selectOnly />
			</div>
			<div
				className={
					!isLoading && topics.length === 0 ? [styles.topicsList, styles.empty].join(' ') : styles.topicsList
				}
			>
				<div className={styles.currentTopicContainer}>
					{!isLoading && topics.length === 0 ? (
						<div className={styles.emptyTopics}>You have no {typeTitle.toLocaleLowerCase()} topics.</div>
					) : (
						totalTopicsTitle
					)}
					{topics.length > 0 && (
						<div className={styles.topicListContainer}>
							{topics.map((topic, i) => (
								<IonItem
									key={topic}
									lines={i === topics.length - 1 ? 'none' : 'full'}
									className={styles.ionItem}
								>
									<div className={styles.topicItem}>
										<span>{topic}</span>
										<span
											className={styles.removeIconWrapper}
											onClick={() => {
												onRemoveTopic(topic);
											}}
										>
											<IconClose size="small" />
										</span>
									</div>
								</IonItem>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export function TopicPreferencesPage(): JSX.Element {
	const history = useHistory();
	const {
		topicPreferences: originalTopicPreferences,
		updateTopicPreferences,
		isError,
		isSuccess
	} = useTopicPreferences();

	const [localTopicPreferences, setLocalTopicPreferences] = useState(originalTopicPreferences);
	const { following, hidden } = localTopicPreferences ?? { following: [], hidden: [] };
	const { topic: prefillTopic } = useParams<{ topic?: string }>();

	useEffect(() => {
		setLocalTopicPreferences(originalTopicPreferences);
	}, [originalTopicPreferences]);

	const handleAction = useCallback(
		async (actionFunc: () => any) => {
			try {
				await actionFunc();
			} catch (error) {
				setLocalTopicPreferences(originalTopicPreferences);
			}
		},
		[originalTopicPreferences]
	);

	const handleUnFollow = useCallback(
		(topic: string) => {
			const updatedFollowing = following.filter(followedTopic => followedTopic !== topic);
			setLocalTopicPreferences({
				...localTopicPreferences,
				following: updatedFollowing,
				hidden: localTopicPreferences?.hidden || []
			});

			handleAction(async () => {
				await updateTopicPreferences({
					...localTopicPreferences,
					following: updatedFollowing,
					hidden: localTopicPreferences?.hidden || []
				});
			});
		},
		[localTopicPreferences, following, updateTopicPreferences, handleAction]
	);

	const handleUnhide = useCallback(
		(topic: string) => {
			const updatedHidden = (hidden || []).filter(hiddenTopic => hiddenTopic !== topic);
			setLocalTopicPreferences({
				...localTopicPreferences,
				hidden: updatedHidden,
				following: localTopicPreferences?.following || []
			});

			handleAction(async () => {
				await updateTopicPreferences({
					...localTopicPreferences,
					hidden: updatedHidden,
					following: localTopicPreferences?.following || []
				});
			});
		},
		[localTopicPreferences, hidden, updateTopicPreferences, handleAction]
	);

	const handleHide = useCallback(
		(topic: string) => {
			const updatedHidden = Array.from(new Set([...(hidden || []), topic]));
			const updatedFollowing = (following || []).filter(followingTopic => followingTopic !== topic);
			setLocalTopicPreferences({
				...localTopicPreferences,
				hidden: updatedHidden,
				following: updatedFollowing
			});

			handleAction(async () => {
				await updateTopicPreferences({
					...localTopicPreferences,
					hidden: updatedHidden,
					following: updatedFollowing
				});
			});
		},
		[localTopicPreferences, hidden, following, updateTopicPreferences, handleAction]
	);

	const handleFollow = useCallback(
		(topic: string) => {
			const updatedFollowing = Array.from(new Set([...(following || []), topic]));
			const updatedHidden = (hidden || []).filter(hiddenTopic => hiddenTopic !== topic);
			setLocalTopicPreferences({
				...localTopicPreferences,
				following: updatedFollowing,
				hidden: updatedHidden
			});

			handleAction(async () => {
				await updateTopicPreferences({
					...localTopicPreferences,
					following: updatedFollowing,
					hidden: updatedHidden
				});
			});
		},
		[localTopicPreferences, following, hidden, updateTopicPreferences, handleAction]
	);

	return (
		<QuirkIonPage name="Topics Preferences">
			<Header left={<IconButton icon={<IconArrowBack />} onClick={() => history.goBack()} />} title="My Topics" />
			<QuirkIonContent scroll={false}>
				<>
					<Tabs>
						<Tab title="Subscribed">
							<TopicPreferencesSection
								topics={following}
								type={'following'}
								onRemoveTopic={handleUnFollow}
								onAddTopic={handleFollow}
								prefillTopic={prefillTopic}
							/>
						</Tab>
						<Tab title="Hidden">
							<TopicPreferencesSection
								topics={hidden}
								type={'hidden'}
								onRemoveTopic={handleUnhide}
								onAddTopic={handleHide}
								prefillTopic={prefillTopic}
							/>
						</Tab>
					</Tabs>
					<IonToast
						isOpen={isError || isSuccess}
						duration={1500}
						message={isError ? 'Error updating topics' : 'Topics updated'}
						position="top"
					/>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
}
